import { DestroyRef, DOCUMENT, inject, Injectable } from '@angular/core';
import { Meta, type MetaDefinition, Title } from '@angular/platform-browser';
import { Router } from '@angular/router';
import { ENVIRONMENT_SETTINGS } from '@reconcept-platform/env';
import { encodeRFC3986 } from '../encode.utils';
import { typedFromEntries } from '../object.utils';

/**
 * https://developers.google.com/search/docs/appearance/structured-data/event
 */
const EVENT_ATTENDANCE_MODES: Record<NonNullable<EventMetadata['eventAttendanceMode']>, string> = {
  offline: 'https://schema.org/OfflineEventAttendanceMode',
  online: 'https://schema.org/OnlineEventAttendanceMode',
  mixed: 'https://schema.org/MixedEventAttendanceMode',
};

const EVENT_STATUSES: Record<NonNullable<EventMetadata['eventStatus']>, string> = {
  scheduled: 'https://schema.org/EventScheduled',
  cancelled: 'https://schema.org/EventCancelled',
  postponed: 'https://schema.org/EventPostponed',
  rescheduled: 'https://schema.org/EventRescheduled',
  movedOnline: 'https://schema.org/EventMovedOnline',
};

// @ts-expect-error TS2339 property does not exist
const EVENT_OFFER_AVAILABILITIES: Record<NonNullable<EventMetadata['offers']['availability']>, string> = {
  inStock: 'https://schema.org/InStock',
  soldOut: 'https://schema.org/SoldOut',
  preOrder: 'https://schema.org/PreOrder',
};

const METADATA_KEYS = [
  'og:site_name',
  'og:url',
  'og:locale',
  'twitter:card',
  'og:title',
  'title',
  'og:description',
  'description',
  'og:type',
  'og:image',
  'og:image:url',
  'og:image:secure_url',
  'og:image:type',
  'og:image:width',
  'og:image:height',
] as const;

const STRUCTURED_JSON_KEYS = ['event'] as const;

@Injectable({ providedIn: 'root' })
export class MetadataService {
  private readonly metaService = inject(Meta);
  private readonly titleService = inject(Title);
  private readonly router = inject(Router);
  private readonly environmentSettings = inject(ENVIRONMENT_SETTINGS);
  private readonly document = inject(DOCUMENT);

  setMetadata(metadata: Metadata) {
    const siteName = metadata.siteName ?? this.environmentSettings.applicationName;
    const mergedTitle = [metadata.title, siteName].join(' · ');
    const imageUrl = metadata.image?.url && encodeRFC3986(metadata.image.url);
    this.setMetadataSnapshot({
      event: metadata.event && getEventStructuredJson(metadata.event),
      'og:title': mergedTitle,
      title: mergedTitle,
      'og:description': metadata.description,
      description: metadata.description,
      'og:site_name': siteName,
      'og:url': this.getCurrentHref(),
      'og:locale': metadata.locale ?? 'nl',
      'twitter:card': 'summary_large_image',
      'og:type': 'website',
      'og:image': imageUrl,
      'og:image:url': imageUrl,
      'og:image:secure_url': imageUrl,
      'og:image:type': metadata.image?.mime,
      'og:image:width': metadata.image?.width?.toString(),
      'og:image:height': metadata.image?.height?.toString(),
    });
  }

  setMetadataSnapshot(metadata: MetadataSnapshot) {
    const previous = this.getMetadataSnapshot();

    for (const name of METADATA_KEYS) {
      const content = metadata[name];
      if (content) {
        const definition: MetaDefinition = { name, property: name, content };
        if (previous[name]) {
          this.metaService.updateTag(definition);
        } else {
          this.metaService.addTag(definition);
        }
      } else {
        this.metaService.removeTag(this.getMetaAttrSelector(name));
      }
    }

    for (const key of STRUCTURED_JSON_KEYS) {
      const content = metadata[key];
      this.setStructuredJson(key, content && JSON.stringify(content));
    }

    // @ts-expect-error TS2345 argument not assignable
    this.titleService.setTitle(metadata.title);
  }

  getMetadataSnapshot() {
    return {
      ...typedFromEntries(
        METADATA_KEYS.map((name) => [name, this.metaService.getTag(this.getMetaAttrSelector(name))?.content]),
      ),
      ...typedFromEntries(STRUCTURED_JSON_KEYS.map((key) => [key, this.getStructuredJsonContent(key)])),
      title: this.titleService.getTitle(),
    };
  }

  resetMetadata() {
    this.setMetadataSnapshot({
      title: this.environmentSettings.applicationName,
    });
  }

  private getCurrentHref() {
    return new URL(this.router.url, this.environmentSettings.portfolioAppURL).toString();
  }

  private getMetaAttrSelector(name: MetadataKey) {
    return `name='${name}'`;
  }

  private getStructuredJsonScriptId(name: StructuredJsonKey) {
    return `structured-${name}`;
  }

  private setStructuredJson(key: StructuredJsonKey, content: string | undefined) {
    const scriptElementId = this.getStructuredJsonScriptId(key);
    if (content) {
      let script = this.document.getElementById(scriptElementId) as HTMLScriptElement | null;

      if (!script) {
        script = this.document.createElement('script');
        script.id = scriptElementId;
        script.type = 'application/ld+json';
        this.document.head.appendChild(script);
      }

      script.textContent = content;
    } else {
      this.document.getElementById(scriptElementId)?.remove();
    }
  }

  private getStructuredJsonContent<K extends StructuredJsonKey>(key: K): MetadataSnapshot[K] {
    const json = this.document.getElementById(this.getStructuredJsonScriptId(key))?.textContent;
    return json ? JSON.parse(json) : undefined;
  }
}

@Injectable()
export class MetadataControl {
  private readonly metadataService = inject(MetadataService);
  private readonly destroyRef = inject(DestroyRef);

  constructor() {
    this.destroyRef.onDestroy(() => this.restoreMetadata());
  }

  setMetadata(metadata: Metadata) {
    this.metadataService.setMetadata(metadata);
  }

  restoreMetadata() {
    this.metadataService.resetMetadata();
  }
}

export interface Metadata {
  title: string;
  siteName?: string;
  description?: string;
  image?: ImageMetadata;
  locale?: string;
  event?: EventMetadata;
}

export interface ImageMetadata {
  url?: string;
  mime?: string;
  width?: number;
  height?: number;
  alt?: string;
}

/**
 * https://developers.google.com/search/docs/appearance/structured-data/event
 */
export interface EventMetadata {
  name: string;
  startDate: string;
  endDate?: string;
  description?: string;
  image?: string[];
  location?: {
    name: string;
    address?: string;
    url?: string;
  };
  organizer?: {
    name: string;
    url?: string;
  };
  offers?: {
    url?: string;
    price?: string;
    priceCurrency?: string;
    availability?: 'inStock' | 'soldOut' | 'preOrder';
    validFrom?: string;
  };
  eventAttendanceMode?: 'offline' | 'online' | 'mixed';
  eventStatus?: 'scheduled' | 'cancelled' | 'postponed' | 'rescheduled' | 'movedOnline';
}

export type MetadataKey = (typeof METADATA_KEYS)[number];
export type StructuredJsonKey = (typeof STRUCTURED_JSON_KEYS)[number];
export interface MetadataSnapshot extends Partial<Record<MetadataKey, string>> {
  event?: ReturnType<typeof getEventStructuredJson>;
}

function getEventStructuredJson(event: EventMetadata) {
  return {
    '@context': 'https://schema.org',
    '@type': 'Event',
    name: event.name,
    startDate: event.startDate,
    endDate: event.endDate,
    description: event.description,
    image: event.image,
    eventAttendanceMode: event.eventAttendanceMode && EVENT_ATTENDANCE_MODES[event.eventAttendanceMode],
    eventStatus: EVENT_STATUSES[event.eventStatus ?? 'scheduled'],
    location: event.location && {
      '@type': 'Place',
      name: event.location.name,
      address: event.location.address,
      url: event.location.url,
    },
    organizer: event.organizer && { '@type': 'Organization', ...event.organizer },
    offers: event.offers && {
      '@type': 'Offer',
      url: event.offers.url,
      price: event.offers.price,
      priceCurrency: event.offers.priceCurrency,
      validFrom: event.offers.validFrom,
      availability: event.offers.availability && EVENT_OFFER_AVAILABILITIES[event.offers.availability],
    },
  };
}
